/**
 * /earthworks/rates — the earthworks rate card (GET /earthworks/rates, EW-1).
 *
 * Full width. Filters in the URL: the card (`card=default`, a customer's id,
 * or every card) and the kind. A rate reads in the company's currency, and an
 * absent figure is `—`. New and edit are full pages (RateDetailScreen).
 * Read by the dispatch roles; written by admin / office staff.
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Tags } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useEwCustomers, useEwMeta, useEwRates } from '../../hooks/useEarthworks';
import { queryLoadFailure } from '../../lib/load-failure';
import { canReadEwRates, canWriteEwRates, catalogLabel, ewDay, ewRateText } from '../../lib/earthworks';
import type { EwRate } from '../../types/earthworks';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { useOpenRecord } from '../downhole/recordPage';
import { EW_EYEBROW, EW_INPUT, EwBadge, EwChip, EwChipRow, EwEmpty, EwPanel, EwRegister, type EwColumn } from './EwBits';

/** 0600: an equipment rate that already pays for its operator. */
const INCLUDES_OPERATOR = { label: 'Incl. operator', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' };
const OperatorBadge = ({ r }: { r: EwRate }) =>
  r.includesOperator ? <span data-testid="ew-rate-includes-operator"><EwBadge skin={INCLUDES_OPERATOR} value="includes_operator" /></span> : null;

/** What a rate prices beyond its kind: the crew role, the unit, the equipment type or the material. */
export function ewRateAppliesTo(r: EwRate, crewRoles?: { value: string; label: string }[]) {
  if (r.crewRole) return catalogLabel(crewRoles, r.crewRole);
  if (r.equipmentId) return r.equipmentUnitNumber ? `${r.equipmentUnitNumber} · ${r.equipmentName ?? '—'}` : r.equipmentName || '—';
  if (r.equipmentTypeId) return r.equipmentTypeName || '—';
  if (r.material) return r.material;
  return '—';
}

export default function EwRatesScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const canRead = canReadEwRates(user?.role);
  const canWrite = canWriteEwRates(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const card = searchParams.get('card') ?? '';
  const kind = searchParams.get('kind') ?? '';
  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };
  const catalog = useEwMeta().data?.data ?? null;
  const customers = useEwCustomers(canRead).data ?? [];
  const listQuery = useEwRates({ customer_id: card || undefined, kind: kind || undefined }, canRead);
  const allQuery = useEwRates(undefined, canRead);
  const rates = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (r: EwRate) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const today = allQuery.data?.meta.today ?? null;
  const live = (r: EwRate) => !!today && !!r.effectiveFrom && r.effectiveFrom <= today && (!r.effectiveTo || r.effectiveTo >= today);
  const filtered = !!(card || kind);
  const unitLabel = (r: EwRate) => r.unitLabel || catalogLabel(catalog?.priceUnits, r.unit);

  const columns: EwColumn<EwRate>[] = [
    { key: 'code', header: 'Code', render: (r) => (<><p className="font-mono font-extrabold text-white">{r.code || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{r.name || '—'}</p></>) },
    { key: 'card', header: 'Card', render: (r) => (r.customerId ? r.customerName || '—' : 'Default card') },
    { key: 'kind', header: 'Kind', render: (r) => (<><p>{r.kindLabel || catalogLabel(catalog?.priceKinds, r.kind)}</p><OperatorBadge r={r} /></>) },
    { key: 'applies', header: 'Applies to', render: (r) => ewRateAppliesTo(r, catalog?.crewRoles) },
    { key: 'rate', header: 'Rate', align: 'right', render: (r) => <span className="font-mono">{ewRateText(r.rate, r.currency, unitLabel(r))}</span> },
    { key: 'pass', header: 'Passthrough', render: (r) => (r.passthrough ? (r.markupPct != null ? `Yes · +${r.markupPct}%` : 'Yes') : 'No') },
    { key: 'period', header: 'Effective', render: (r) => `${ewDay(r.effectiveFrom)} – ${r.effectiveTo ? ewDay(r.effectiveTo) : 'no end'}` },
  ];

  return (
    <div data-testid="ew-rates-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={EW_EYEBROW}
        title="Rate Card"
        description="What the company bills — labour, equipment, trucking, material and disposal — on the default card or a customer's own."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'Rates', tone: 'neutral', loading: allQuery.isLoading, value: countOf(() => true) },
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'In effect', tone: 'accent', loading: allQuery.isLoading, value: countOf(live) },
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'Customer', tone: 'neutral', loading: allQuery.isLoading, value: countOf((r) => !!r.customerId) },
        ]}
        actions={
          canWrite ? (
            <button
              type="button"
              className={DH_BAND_SECONDARY}
              onClick={() => navigate(`/earthworks/rates/new${card && card !== 'default' ? `?customerId=${card}` : ''}`)}
            >
              <Plus className="size-4" aria-hidden="true" />
              New rate
            </button>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canRead ? (
            <EwPanel eyebrow="Rate card" title="Rates">
              <EwEmpty>The rate card carries prices, so only the office, supervisors and dispatch see it.</EwEmpty>
            </EwPanel>
          ) : (
            <>
              <EwChipRow label="Filter by kind">
                <EwChip label="All kinds" active={!kind} onClick={() => setParam('kind', '')} />
                {(catalog?.priceKinds ?? []).map((k) => (
                  <EwChip key={k.value} label={k.label} active={kind === k.value} onClick={() => setParam('kind', k.value)} count={allQuery.isLoading ? undefined : countOf((r) => r.kind === k.value)} />
                ))}
              </EwChipRow>
              <div className="grid gap-3 sm:grid-cols-[16rem]">
                <select className={EW_INPUT} aria-label="Filter by card" value={card} onChange={(e) => setParam('card', e.target.value)}>
                  <option value="">Every card</option>
                  <option value="default">Default card</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <EwPanel eyebrow="Rate card" title="Rates" testId="ew-rate-register" footer={!listQuery.isLoading && rates.length ? `${rates.length} rate${rates.length === 1 ? '' : 's'}` : undefined}>
                <EwRegister
                  query={listQuery}
                  rows={rates}
                  columns={columns}
                  rail={(r) => (live(r) ? 'border-l-fi-green' : 'border-l-fi-steel')}
                  onOpen={(r) => openRecord(`/earthworks/rates/${r.id}`)}
                  subject="rates"
                  testId="ew-rate"
                  minWidth="min-w-[1000px]"
                  empty={filtered ? <EwEmpty>No rate matches these filters.</EwEmpty> : <EwEmpty>No rates yet. Add the first one to price field tickets.</EwEmpty>}
                  card={(r) => (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-mono text-sm font-extrabold text-white">{r.code || '—'}</p>
                          <p className="mt-0.5 text-xs text-fieldiq-muted">{r.name || '—'} · {r.customerId ? r.customerName || '—' : 'Default card'}</p>
                        </div>
                        <span className="font-mono text-sm text-white">{ewRateText(r.rate, r.currency, unitLabel(r))}</span>
                      </div>
                      <p className="mt-2 text-xs text-fieldiq-text">
                        {r.kindLabel || catalogLabel(catalog?.priceKinds, r.kind)} · {ewRateAppliesTo(r, catalog?.crewRoles)} · from {ewDay(r.effectiveFrom)}
                      </p>
                      <OperatorBadge r={r} />
                    </>
                  )}
                />
              </EwPanel>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
